import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { AvatarComponent } from '../avatar/avatar.component';
import { PromptCardComponent } from '../../toasts-and-cards/prompt-card/prompt-card.component';

// services
import { KeycloakAdminService } from '../../../services/keycloak/keycloak.service';
import { UserProfileService } from '../../../services/user/user-profile.service';
import { SettingsService } from '../../../services/settings/settings.service';
import { TBlkCxpRole } from '../../../types/settingsDataModule';
import {
  ORGANIZATION_TAB_ROLES,
  hasAnyRole,
  roleList,
} from '../../../pages/organization/settings/settings-roles';

/**
 * The account menu in the top bar: who is signed in, to which organization,
 * with which role, and the way to Profile, Organization settings and Sign out.
 * A menu button: Enter, Space or Arrow down opens it on the first item, arrows
 * move, Escape closes and puts focus back on the avatar.
 */
@Component({
  selector: 'app-profile-avatar',
  standalone: true,
  imports: [AvatarComponent, RouterLink, NgClickOutsideDirective, PromptCardComponent],
  templateUrl: './profile-avatar.component.html',
  styleUrl: './profile-avatar.component.css',
})
export class ProfileAvatarComponent implements OnInit {
  // services
  kcService = inject(KeycloakAdminService);
  settingsService = inject(SettingsService);
  userService = inject(UserProfileService);
  private destroyRef = inject(DestroyRef);

  @ViewChild('trigger') private trigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('menu') private menu?: ElementRef<HTMLElement>;

  // state
  openOptions: boolean = false;
  openLogout: boolean = false;
  profileImageUrl: string | null = null;

  /** The portal dims the page while the menu is open. */
  @Output() optionsStateChange = new EventEmitter<boolean>();

  userName: string = '';
  /** "Administrator, Content manager". */
  roleText = '';
  canSeeOrganization = false;

  get userDetails() {
    return this.userService.userDetails;
  }

  get email(): string {
    return this.profileEmail || this.userDetails?.email || '';
  }

  /** Read from storage when the menu opens, not on every change detection. */
  orgName = '';

  private profileEmail = '';

  toggleOPenOptions() {
    this.openOptions ? this.closeOptions() : this.openMenu(false);
  }

  closeOptions() {
    if (!this.openOptions) return;
    this.openOptions = false;
    this.optionsStateChange.emit(this.openOptions);
  }

  /** Opens the menu; from the keyboard, focus moves to an item. */
  private openMenu(focusLast: boolean) {
    if (!this.openOptions) {
      this.orgName = this.userService.getInstitutionName || '';
      this.openOptions = true;
      this.optionsStateChange.emit(this.openOptions);
    }
    setTimeout(() => this.focusItem(focusLast ? -1 : 0));
  }

  openLogoutPrompt() {
    this.closeOptions();
    this.openLogout = true;
  }

  closeLogoutPrompt() {
    this.openLogout = false;
    setTimeout(() => this.trigger?.nativeElement.focus());
  }

  onClickedOutside() {
    this.closeOptions();
  }

  handleLogout() {
    this.kcService.logout();
    this.closeOptions();
  }

  onTriggerKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.openMenu(event.key === 'ArrowUp');
    } else if (event.key === 'Escape' && this.openOptions) {
      event.preventDefault();
      this.closeOptions();
    }
  }

  onMenuKeydown(event: KeyboardEvent) {
    const items = this.items();
    const index = items.indexOf(document.activeElement as HTMLElement);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusItem(index < 0 ? 0 : (index + 1) % items.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusItem(index <= 0 ? items.length - 1 : index - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.focusItem(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusItem(-1);
        break;
      case 'Escape':
        event.preventDefault();
        this.closeOptions();
        this.trigger?.nativeElement.focus();
        break;
      case 'Tab':
        this.closeOptions();
        break;
    }
  }

  private items(): HTMLElement[] {
    const root = this.menu?.nativeElement;
    return root ? Array.from(root.querySelectorAll<HTMLElement>('[role="menuitem"]')) : [];
  }

  private focusItem(index: number) {
    const items = this.items();
    if (!items.length) return;
    const target = index < 0 ? items[items.length - 1] : items[Math.min(index, items.length - 1)];
    target?.focus();
  }

  fetchUserProfileImage() {
    const userId = this.userDetails?.sub;
    if (!userId) {
      return;
    }

    this.settingsService.getUserProfileImage(userId).subscribe({
      error: () => {
        // No photo, or it failed: initials show instead.
      },
    });
  }

  ngOnInit() {
    this.settingsService.fetchUserProfile();
    this.fetchUserProfileImage();

    // A removed photo arrives as null and clears the image.
    this.settingsService.getUserProfileImageData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((imageData) => {
        this.profileImageUrl = imageData || null;
      });

    this.settingsService.getUserProfileData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (!data) {
          return;
        }
        this.userName = `${data?.firstName || ''} ${data?.lastName || ''}`.trim();
        this.profileEmail = data?.email || '';
      });

    this.settingsService.getUserRoles
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((roles: TBlkCxpRole[]) => {
        this.roleText = roleList(roles);
        this.canSeeOrganization = hasAnyRole(roles, ORGANIZATION_TAB_ROLES);
      });
  }
}
